import { Box, Stack, TextField, Typography } from "@mui/material";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FC } from "react";
import { useCallCenterShift } from "../../contexts/CallCenterShiftProvider";
import { CC_BRAND } from "./call-center-brand";
import { CcButton, CcEyebrow, CcPanel } from "./CallCenterUi";
import { CC, ccFieldSx } from "./call-center-ui.theme";
import { TOUR_CHAT } from "./call-center-tour-fixtures";

const KIND_LABEL: Record<string, string> = {
  DETAILS_CARD: "כרטיס משימה",
  INCOMING_CALL: "שיחה נכנסת",
  SYSTEM: "מערכת",
};

export const CallCenterChat: FC<{ example?: boolean }> = ({ example = false }) => {
  const { workbench, setUnreadChat } = useCallCenterShift();
  const [body, setBody] = useState("");
  const threadRef = useRef<HTMLDivElement>(null);
  const baselineRef = useRef<number | null>(null);
  const [freshFrom, setFreshFrom] = useState<number | null>(null);
  const chatQuery = useQuery({
    queryKey: ["call-center-chat", workbench?.volunteer.id],
    queryFn: async () => {
      const { data } = await linko.callCenter.queries.listChat({});
      return data;
    },
    enabled: !example,
    refetchInterval: example ? false : 60_000,
  });

  useEffect(() => {
    if (!example) setUnreadChat(0);
  }, [example, setUnreadChat]);

  useEffect(() => {
    baselineRef.current = null;
    setFreshFrom(null);
  }, [workbench?.volunteer.id]);

  const messages = example
    ? TOUR_CHAT.map((msg) => ({
        id: msg.id,
        kind: msg.kind,
        body: msg.body,
        createdAt: msg.createdAt,
        senderManagerId: msg.fromCoordinator ? "tour-coordinator" : null,
      }))
    : (chatQuery.data ?? []);

  useEffect(() => {
    if (example || !chatQuery.data) return;
    if (baselineRef.current == null) {
      baselineRef.current = chatQuery.data.length;
    } else if (chatQuery.data.length > baselineRef.current) {
      setFreshFrom(baselineRef.current);
    }
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [chatQuery.data, example]);

  const send = async () => {
    if (example || !body.trim()) return;
    await linko.callCenter.mutations.sendChat({ kind: "TEXT", body });
    setBody("");
    setUnreadChat(0);
    await chatQuery.refetch();
  };

  return (
    <Stack
      gap={2}
      sx={{
        p: { xs: 1.5, md: 2.5 },
        height: "100%",
        minHeight: 0,
        flex: 1,
      }}
      data-tour="cc-chat-panel"
    >
      <Stack gap={0.5}>
        <CcEyebrow>{CC_BRAND}</CcEyebrow>
        <Typography
          sx={{
            fontFamily: CC.font,
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: "-0.03em",
            color: CC.ink,
          }}
        >
          צ'אט
        </Typography>
      </Stack>

      <CcPanel
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          ref={threadRef}
          data-tour="cc-chat-thread"
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.1,
            p: 2,
          }}
        >
          {messages.length === 0 ? (
            <Typography
              sx={{
                fontFamily: CC.font,
                color: CC.inkMuted,
                textAlign: "center",
                py: 6,
              }}
            >
              עדיין אין הודעות. אפשר לשלוח שאלה, כרטיס משימה או פרטי שיחה נכנסת.
            </Typography>
          ) : (
            messages.map((msg, index) => {
              const mine = !msg.senderManagerId;
              const fresh = !mine && freshFrom != null && index >= freshFrom;
              return (
                <Box
                  key={msg.id}
                  sx={{
                    alignSelf: mine ? "flex-end" : "flex-start",
                    maxWidth: "min(78%, 520px)",
                    px: 1.75,
                    py: 1.2,
                    borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    bgcolor: mine ? CC.ink : fresh ? CC.goldSoft : "rgba(5,32,76,0.05)",
                    color: mine ? CC.paper : CC.ink,
                    border: mine ? 0 : `1px solid ${fresh ? "rgba(165,124,50,0.55)" : CC.line}`,
                    boxShadow: fresh ? "0 0 0 3px rgba(165,124,50,0.16)" : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: CC.font,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      opacity: 0.7,
                      mb: 0.4,
                    }}
                  >
                    {fresh ? "חדש · " : ""}
                    {KIND_LABEL[msg.kind] ?? ""}{" "}
                    {new Date(msg.createdAt).toLocaleTimeString("he-IL", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                  <Typography sx={{ fontFamily: CC.font, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                    {msg.body}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
        <Stack
          data-tour="cc-chat-compose"
          gap={1}
          sx={{
            p: 1.5,
            borderTop: `1px solid ${CC.line}`,
            bgcolor: CC.paper,
          }}
        >
          <Stack direction="row" gap={1}>
            <TextField
              fullWidth
              placeholder="הודעה למנהלת"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              sx={ccFieldSx}
            />
            <CcButton onClick={() => void send()} sx={{ px: 3 }}>
              שליחה
            </CcButton>
          </Stack>
          <CcButton
            data-tour="cc-chat-incoming"
            kind="soft"
            sx={{ alignSelf: "flex-start" }}
            onClick={async () => {
              await linko.callCenter.mutations.sendChat({
                kind: "INCOMING_CALL",
                body: "שיחה נכנסת - פרטים בדרך",
              });
              await chatQuery.refetch();
            }}
          >
            שליחת פרטי שיחה נכנסת
          </CcButton>
        </Stack>
      </CcPanel>
    </Stack>
  );
};
